/* eslint-disable @typescript-eslint/no-var-requires */
import * as React from "react";
import { MainLayout } from "../../components/Layout";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { useYupValidationResolver } from "../../custom-hooks/form-resolover";
import SmartInput from "../../components/SmartInput";
import SmartForm from "../../components/SmartForm";
import SmartButton from "../../components/SmartButton";
import "../PaymentMake/styles.scss";
import Headline from "../../components/Headline";
import { PaymentType } from "../../generated/lib-domain/paymentZeus";
import RegisterAddress from "../PaymentMake/registerAddress";
import {
    CardPaymentPlan,
    Prefecture,
    UserAddress
} from "../../generated/UserAPIAgent";
import {
    IAPPaymentMethod,
    PaymentMethod
} from "../../generated/lib-domain/iap";
import { PlanPrice } from "../../components/PlanPrice";
import { UserStatus } from "../../generated/lib-domain/user";
import CampaignMessage from "../../components/Messages/CampaignMessage";
import { Message } from "../../types/common";
import FormErrorMessage from "../../components/FormErrorMessage";
import {
    ThreeDSecureAuth,
    ThreeDSecureAuthResult
} from "../../components/ThreeDSecureAuth/ThreeDSecureAuth";

export interface PaymentInfoState {
    paymentType: PaymentType | null;
    orderedAmount: number | null;
    primaryPaymentMethod: PaymentMethod;
    cardPaymentPlan: CardPaymentPlan;
}

interface PaymentMake3DSPageProps {
    onSubmitCreditCardInfo: (creditCard: {
        cardNumber: string;
        expiresMonth: string;
        expiresYear: string;
        securityNo: string;
    }) => Promise<void>;
    onSubmitAddress: (
        zip: string,
        pref: Prefecture,
        city: string,
        address: string,
        building?: string
    ) => Promise<void>;
    isChangeCreditCardInfo: boolean;
    paymentMakeIsLoading: boolean;
    paymentMakeIsFinished: boolean;
    registerAddressIsLoading: boolean;
    paymentInfoState: PaymentInfoState;
    userAddress: UserAddress;
    userStatus: UserStatus;
    campaignMessage: Message | null;
    show3DSAuth: boolean;
    sessionId: string;
    md: string;
    termUrl: string;
    iframeUrl: string;
    clientIp: string;
    onAuthComplete: (result: ThreeDSecureAuthResult) => void;
    onAuthError: (error: { errorCode?: string; errorMessage?: string }) => void;
    threeDSProcessing: boolean;
}

const validationSchema = yup.object().shape({
    card: yup
        .string()
        .matches(/^[0-9]{14,16}$/, "カード番号が正しく入力されていません")
        .required("カード番号が正しく入力されていません")
        .typeError("カード番号が正しく入力されていません"),
    month: yup
        .string()
        .matches(
            /^(0[1-9]|1[0-2])$/,
            "有効期限（月）が正しく入力されていません"
        )
        .required("有効期限（月）が正しく入力されていません")
        .typeError("有効期限（月）が正しく入力されていません"),
    year: yup
        .number()
        .min(21, "有効期限（年）が正しく入力されていません")
        .max(99, "有効期限（年）が正しく入力されていません")
        .integer()
        .required("有効期限（年）が正しく入力されていません")
        .typeError("有効期限（年）が正しく入力されていません"),
    csc: yup
        .string()
        .matches(/^[0-9]{3,4}$/, "セキュリティコードが正しく入力されていません")
        .required("セキュリティコードが正しく入力されていません")
});

const logoCardSrc = require("../../assets/img/logo_card.png").default;
const creditBackSrc = require("../../assets/img/security-code.png").default;

const PaymentMake3DS = (props: PaymentMake3DSPageProps) => {
    const validationResolver = useYupValidationResolver(validationSchema);
    const { register, handleSubmit, errors, formState, watch } = useForm({
        resolver: validationResolver,
        mode: "onChange"
    });

    const onSubmit = (data) => {
        props
            .onSubmitCreditCardInfo({
                cardNumber: data.card,
                expiresMonth: data.month,
                expiresYear: data.year.toString(),
                securityNo: data.csc
            })
            .then();
    };

    watch();

    const buttonText = () => {
        if (props.userStatus === UserStatus.Created) {
            return "有料会員プランに登録する";
        }

        if (props.isChangeCreditCardInfo) {
            return "変更する";
        }

        if (
            props.paymentInfoState.primaryPaymentMethod ===
                IAPPaymentMethod.IOS_IAP ||
            props.paymentInfoState.paymentType === PaymentType.First
        ) {
            return "次へ";
        }

        return (
            <>
                <PlanPrice
                    cardPaymentPlan={props.paymentInfoState.cardPaymentPlan}
                />{" "}
                購入する
            </>
        );
    };

    if (
        props.paymentInfoState.paymentType === PaymentType.First &&
        props.paymentMakeIsFinished
    ) {
        return (
            <RegisterAddress
                paymentType={props.paymentInfoState.paymentType}
                onSubmitAddress={props.onSubmitAddress}
                isLoading={props.registerAddressIsLoading}
                userAddress={props.userAddress}
                cardPaymentPlan={props.paymentInfoState.cardPaymentPlan}
            />
        );
    }

    return (
        <MainLayout>
            <Headline>
                {props.isChangeCreditCardInfo
                    ? "クレジットカードの変更"
                    : "クレジットカードの登録"}
            </Headline>
            {props.userStatus === UserStatus.Created &&
                props.campaignMessage && (
                    <CampaignMessage message={props.campaignMessage} />
                )}
            <section className="payment-make">
                <SmartForm onSubmit={handleSubmit(onSubmit)}>
                    <div className="form-item">
                        <label className="input-label">
                            <div>カード番号</div>
                            <img
                                src={logoCardSrc}
                                alt="credit card logos"
                                className="credit-card-logos"
                            />
                        </label>
                        <SmartInput
                            type="number"
                            className="card-input"
                            name="card"
                            error={errors["card"]}
                            register={register}
                            placeholder="例: 1234 5678 9123 1234"
                        />
                    </div>

                    <div className="form-item">
                        <label className="input-label">有効期限</label>
                        <div className="flex">
                            <div className="card-expire">
                                <SmartInput
                                    type="number"
                                    className="card-input"
                                    name="month"
                                    register={register}
                                    placeholder="例: 01"
                                />
                            </div>
                            <div className="expire-text">月</div>
                            <div className="expire-text">/</div>
                            <div className="card-expire">
                                <SmartInput
                                    type="number"
                                    className="card-input"
                                    name="year"
                                    register={register}
                                    placeholder="例: 22"
                                />
                            </div>
                            <div className="expire-text">年</div>
                        </div>
                        {errors["month"] && (
                            <FormErrorMessage
                                message={errors["month"].message}
                            />
                        )}
                        {errors["year"] && (
                            <FormErrorMessage
                                message={errors["year"].message}
                            />
                        )}
                    </div>
                    <div className="form-item">
                        <label className="input-label">
                            セキュリティコード
                        </label>
                        <SmartInput
                            type="number"
                            className="card-input"
                            name="csc"
                            error={errors["csc"]}
                            register={register}
                            placeholder="例: 123"
                        />
                        <div className="csc-description">
                            <img
                                src={creditBackSrc}
                                alt="behind the credit card"
                            />
                            <div>
                                セキュリティコードとはカード裏面に記載されている、3桁（または4桁）の番号です。
                            </div>
                        </div>
                    </div>
                    <SmartButton
                        type="submit"
                        disabled={
                            !formState.isValid ||
                            formState.isSubmitting ||
                            props.threeDSProcessing
                        }
                        isLoading={props.paymentMakeIsLoading}
                    >
                        {buttonText()}
                    </SmartButton>
                </SmartForm>
            </section>

            {props.show3DSAuth && props.sessionId && (
                <ThreeDSecureAuth
                    sessionId={props.sessionId}
                    md={props.md}
                    termUrl={props.termUrl}
                    iframeUrl={props.iframeUrl}
                    clientIp={props.clientIp}
                    onComplete={props.onAuthComplete}
                    onError={props.onAuthError}
                />
            )}
        </MainLayout>
    );
};

export default PaymentMake3DS;
